import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  IconButton,
  makeStyles,
  Divider,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { format } from 'date-fns';
import api from '../../services/api';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    margin: 0,
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  closeButton: {
    color: theme.palette.grey[500],
  },
  emojiSection: {
    marginBottom: theme.spacing(2),
  },
  emojiHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
  },
  emojiIcon: {
    fontSize: '24px',
    marginRight: theme.spacing(1),
  },
  emojiCount: {
    color: theme.palette.text.secondary,
    fontSize: '14px',
    marginLeft: theme.spacing(0.5),
  },
  reactionItem: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  userName: {
    fontWeight: 500,
  },
  reactionTime: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
  },
  youLabel: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

const ReactionDetailsModal = ({ open, onClose, reactions, isGroup, messagesList }) => {
  const classes = useStyles();
  
  // Cache para nomes de contatos já buscados
  const [contactNamesCache, setContactNamesCache] = useState({});
  
  // Estado para armazenar nomes processados
  const [processedReactions, setProcessedReactions] = useState([]);

  // Função avançada para encontrar nome do contato
  const findContactName = useCallback(async (contactId) => {
    if (!contactId) return null;
    
    // Verificar cache primeiro
    if (contactNamesCache[contactId]) {
      return contactNamesCache[contactId];
    }
    
    // 1. Procurar em todas as mensagens por um contato com este ID
    if (messagesList) {
      for (const message of messagesList) {
        if (message.contactId === contactId && message.contact?.name) {
          const name = message.contact.name;
          setContactNamesCache(prev => ({ ...prev, [contactId]: name }));
          return name;
        }
      }
      
      // 2. Procurar em reações de outras mensagens
      for (const message of messagesList) {
        if (message.reactionsMsg) {
          for (const reaction of message.reactionsMsg) {
            if (reaction.contactId === contactId && reaction.contact?.name) {
              const name = reaction.contact.name;
              setContactNamesCache(prev => ({ ...prev, [contactId]: name }));
              return name;
            }
          }
        }
      }
    }
      // 3. Tentar buscar na API como último recurso
    try {
      const response = await api.get(`/contacts/${contactId}`);
      if (response.data && response.data.name && response.data.name !== `Contato ${contactId}`) {
        const name = response.data.name;
        setContactNamesCache(prev => ({ ...prev, [contactId]: name }));
        return name;
      }
    } catch (error) {
      // Silently fail - this is expected for some contacts
    }
    return null;
  }, [messagesList, contactNamesCache]);

  const formatReactionTime = (dateString) => {
    try {
      return format(new Date(dateString), 'HH:mm');
    } catch (error) {
      return '';
    }
  };
  const getUserName = useCallback(async (reaction) => {
    if (reaction.fromMe) {
      return 'Você';
    }
    
    // 1. Tentar pegar o nome direto da reação
    if (reaction.contact?.name && reaction.contact.name !== `Contato ${reaction.contactId}`) {
      return reaction.contact.name;
    }
    
    // 2. Tentar propriedade contactName
    if (reaction.contactName && reaction.contactName !== `Contato ${reaction.contactId}`) {
      return reaction.contactName;
    }
    
    // 3. Tentar pushName ou displayName (algumas vezes vem do WhatsApp)
    if (reaction.pushName && reaction.pushName.trim() !== '') {
      return reaction.pushName;
    }
    
    if (reaction.displayName && reaction.displayName.trim() !== '') {
      return reaction.displayName;
    }
    
    // 4. Tentar encontrar o nome pelo contactId usando a função avançada
    if (reaction.contactId) {
      const foundName = await findContactName(reaction.contactId);
      if (foundName) {
        return foundName;
      }
    }
    
    // 5. Tentar número do contato (formatado de forma amigável)
    if (reaction.contact?.number) {
      const number = reaction.contact.number;
      // Se tem nome salvo no telefone, prefere nome
      if (reaction.contact.name && reaction.contact.name !== number) {
        return reaction.contact.name;
      }
      // Formatar número de telefone brasileiro
      if (number.startsWith('55') && number.length >= 12) {
        const formattedNumber = number.replace(/^55(\d{2})(\d{4,5})(\d{4})$/, '+55 ($1) $2-$3');
        return formattedNumber !== number ? formattedNumber : number;
      }
      return number;
    }
    
    // 6. Se tem contactId, mostrar de forma mais amigável
    if (reaction.contactId) {
      return `Contato ${reaction.contactId}`;
    }
    
    // 7. Último recurso
    return 'Usuário Desconhecido';
  }, [findContactName]);

  // Efeito para processar nomes de forma assíncrona
  useEffect(() => {
    const processReactionNames = async () => {
      if (!reactions || reactions.length === 0) return;

      const processed = await Promise.all(
        reactions.map(async (reaction) => {
          const userName = await getUserName(reaction);
          return {
            ...reaction,
            userName
          };
        })
      );

      setProcessedReactions(processed);
    };

    processReactionNames();
  }, [reactions, getUserName]);

  // Early return after all hooks
  if (!reactions || reactions.length === 0) {
    return null;
  }

  const getUserAvatar = (userName) => {
    if (userName === 'Você') {
      return 'V'; // "Você"
    }
    
    // Se o nome é "Usuário Desconhecido" ou similar, usar "?"
    if (userName.includes('Desconhecido') || userName.includes('Usuário')) {
      return '?';
    }
    
    // Se é um número de telefone (contém dígitos), usar "#"
    if (/^\+?\d/.test(userName)) {
      return '#';
    }
    
    // Usar primeira letra do nome
    return userName.charAt(0).toUpperCase();
  };
  // Agrupar reações processadas por emoji, garantindo que cada usuário apareça apenas uma vez por emoji
  const groupedReactions = processedReactions.reduce((acc, reaction) => {
    if (!reaction.body || reaction.body.trim() === '') return acc;
    
    if (!acc[reaction.body]) {
      acc[reaction.body] = [];
    }
    
    // Em conversas em grupo, verificar se já existe uma reação deste usuário para este emoji
    const userKey = reaction.fromMe ? 'me' : reaction.contactId;
    const existingIndex = acc[reaction.body].findIndex(r => 
      (r.fromMe && reaction.fromMe) || (!r.fromMe && r.contactId === reaction.contactId)
    );
    
    if (existingIndex !== -1) {
      // Se já existe uma reação deste usuário para este emoji, substituir pela mais recente
      const existing = acc[reaction.body][existingIndex];
      const existingTime = new Date(existing.createdAt);
      const currentTime = new Date(reaction.createdAt);
      
      if (currentTime > existingTime) {
        acc[reaction.body][existingIndex] = reaction;
      }
    } else {
      // Se não existe, adicionar nova reação
      acc[reaction.body].push(reaction);
    }
    
    return acc;
  }, {});

  // Ordenar por quantidade de reações (mais reagidas primeiro)
  const sortedEmojis = Object.keys(groupedReactions).sort(
    (a, b) => groupedReactions[b].length - groupedReactions[a].length
  );
  // Se ainda não processou as reações, mostrar loading
  if (!processedReactions.length && reactions?.length > 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant="h6">
            {isGroup ? 'Reações da mensagem' : 'Reação'}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Typography>Carregando nomes dos contatos...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">
          {isGroup ? 'Reações da mensagem' : 'Reação'}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {sortedEmojis.map((emoji, index) => {
          const emojiReactions = groupedReactions[emoji];
          
          return (
            <div key={emoji} className={classes.emojiSection}>
              <div className={classes.emojiHeader}>
                <span className={classes.emojiIcon}>{emoji}</span>
                <Typography variant="subtitle2">
                  {emojiReactions.length === 1 ? '1 pessoa' : `${emojiReactions.length} pessoas`}
                </Typography>
              </div>
              
              <List dense>
                {emojiReactions
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Mais recentes primeiro
                  .map((reaction, reactionIndex) => (
                    <ListItem key={`${emoji}-${reactionIndex}`} className={classes.reactionItem}>
                      <ListItemAvatar>
                        <Avatar style={{ width: 32, height: 32, fontSize: '14px' }}>
                          {getUserAvatar(reaction.userName)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <span className={reaction.fromMe ? classes.youLabel : classes.userName}>
                            {reaction.userName}
                          </span>
                        }
                        secondary={
                          <span className={classes.reactionTime}>
                            {formatReactionTime(reaction.createdAt)}
                          </span>
                        }
                      />
                    </ListItem>
                  ))}
              </List>
              
              {index < sortedEmojis.length - 1 && <Divider />}
            </div>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};

export default ReactionDetailsModal;
